import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';
import {
  BookOpen, Plus, Edit2, Trash2, Search, Users, GraduationCap, HeartHandshake, Phone, Mail,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ExportButton from '../components/ExportButton';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { CatchupFormDialog } from '../components/catchup/CatchupFormDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  enrolled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  graduated: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  withdrawn: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300',
};

const emptyForm = {
  student_name: '',
  class_grade: '',
  parent_guardian_name: '',
  parent_contact: '',
  parent_email: '',
  class_teacher: '',
  class_teacher_contact: '',
  circumstances: '',
  focus_subjects: '',
  enrollment_date: '',
  status: 'active',
  progress_notes: '',
};

const CatchupLearningPage = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'school_head', 'project_manager']);

  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [stRes, sumRes] = await Promise.all([
        axios.get(`${API_URL}/api/catchup/students`),
        axios.get(`${API_URL}/api/catchup/summary`),
      ]);
      setStudents(stRes.data);
      setSummary(sumRes.data);
    } catch (err) {
      toast.error('Failed to load catch-up students');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, enrollment_date: new Date().toISOString().slice(0, 10) });
    setDialog(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    const next = { ...emptyForm };
    Object.keys(emptyForm).forEach((k) => { next[k] = s[k] || emptyForm[k]; });
    setForm(next);
    setDialog(true);
  };

  const submit = async () => {
    if (!form.student_name) {
      toast.error('Student name is required');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/catchup/students/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await axios.post(`${API_URL}/api/catchup/students`, form);
        toast.success('Enrolled');
      }
      setDialog(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const removeStudent = async (id) => {
    if (!window.confirm('Remove this student from the catch-up programme?')) return;
    try {
      await axios.delete(`${API_URL}/api/catchup/students/${id}`);
      toast.success('Removed');
      load();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const filtered = students.filter((s) => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (s.student_name || '').toLowerCase().includes(q) ||
        (s.class_grade || '').toLowerCase().includes(q) ||
        (s.class_teacher || '').toLowerCase().includes(q) ||
        (s.parent_guardian_name || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        Loading catch-up programme…
      </div>
    );
  }

  const active = summary?.by_status?.active || 0;
  const graduated = summary?.by_status?.graduated || 0;

  return (
    <div className="space-y-6" data-testid="catchup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <HeartHandshake className="h-8 w-8 text-rose-500" />
            Catch-up Learning Programme
          </h1>
          <p className="text-muted-foreground mt-1">
            Foundational support for students who need extra help to reach grade level
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton path="exports/list/catchup-students" filenameBase="suubi_catchup" testId="catchup-export-btn" />
          {canEdit && (
            <Button onClick={openCreate} data-testid="add-catchup-btn">
              <Plus className="h-4 w-4 mr-2" /> Enroll Student
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Total Enrolled
            </CardDescription>
            <CardTitle className="text-3xl">{summary?.total_students || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-green-600">
              <BookOpen className="h-4 w-4" /> Active
            </CardDescription>
            <CardTitle className="text-3xl text-green-600">{active}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-purple-600">
              <GraduationCap className="h-4 w-4" /> Graduated
            </CardDescription>
            <CardTitle className="text-3xl text-purple-600">{graduated}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Distinct Classes</CardDescription>
            <CardTitle className="text-3xl">
              {summary ? Object.keys(summary.by_class).length : 0}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by student, class, teacher, or parent…"
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="catchup-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="catchup-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="enrolled">Enrolled</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="graduated">Graduated</SelectItem>
              <SelectItem value="withdrawn">Withdrawn</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Students table */}
      <Card>
        <CardHeader>
          <CardTitle>Register</CardTitle>
          <CardDescription>{filtered.length} student{filtered.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No students match. {canEdit && 'Click "Enroll Student" to add the first one.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Class Teacher</TableHead>
                    <TableHead>Parent / Guardian</TableHead>
                    <TableHead>Parent Contact</TableHead>
                    <TableHead>Focus</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Enrolled</TableHead>
                    {canEdit && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow key={s.id} data-testid={`catchup-row-${s.id}`}>
                      <TableCell>
                        <div className="font-medium">{s.student_name}</div>
                        {s.circumstances && (
                          <div className="text-xs text-muted-foreground line-clamp-1 max-w-xs">
                            {s.circumstances}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{s.class_grade || '—'}</TableCell>
                      <TableCell>
                        <div>{s.class_teacher || '—'}</div>
                        {s.class_teacher_contact && (
                          <div className="text-xs text-muted-foreground">{s.class_teacher_contact}</div>
                        )}
                      </TableCell>
                      <TableCell>{s.parent_guardian_name || '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col text-xs">
                          {s.parent_contact && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {s.parent_contact}
                            </span>
                          )}
                          {s.parent_email && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="h-3 w-3" /> {s.parent_email}
                            </span>
                          )}
                          {!s.parent_contact && !s.parent_email && '—'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{s.focus_subjects || '—'}</TableCell>
                      <TableCell>
                        <Badge className={cn(STATUS_COLORS[s.status] || '')}>
                          {s.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {s.enrollment_date ? formatDate(s.enrollment_date) : '—'}
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(s)}
                            data-testid={`edit-catchup-${s.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeStudent(s.id)}
                            data-testid={`delete-catchup-${s.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <CatchupFormDialog
        open={dialog}
        onOpenChange={setDialog}
        editing={editing}
        form={form}
        setForm={setForm}
        onSubmit={submit}
      />
    </div>
  );
};

export default CatchupLearningPage;
